/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */
// import Server from './Server'
// import Users from './Users'
import WebSocketPubSub from './WebSocketPubSub'
// import Grid, { Cell } from './utilities/Grid'

/** This class handles sending and receiving messages between users on the server. */
export default class MessageManager {
  /** @private List of action handlers */
  actions = {}

  /** List of channels to unsubscribe from */
  toUnsubscribe = []

  /** List of channels to subscribe to */
  toSubscribe = []

  /** Zone cell size */
  zoneCellSize = 300

  /** Current zone channel ID */
  zoneChannelID = '?'

  /** User-specific channel ID. */
  userChannelID = '?'

  /** Pending requests */
  pendingRequests = {}

  /** Pending Broadcast Responses */
  pendingBroadcastResponses = {}

  /** Statistics */
  numReceived = 0
  numSent = 0
  get numChannels() {
    return this.pubsub.channels.length
  }

  /** Constructor */
  constructor(spaceID, userID) {
    // Create pubsub instance
    this.spaceID = spaceID
    this.userID = userID
  }

  /** Called on startup */
  setup() {
    // Join global server channel
    this.pubsub = new WebSocketPubSub('wss://g-pubsub.spatialweb.net')
    this.pubsub.join(this.spaceID + ':default')

    // Join user's channel
    this.userChannelID = 'user:' + this.userID
    this.pubsub.join(this.userChannelID)

    // Listen for incoming messages
    this.pubsub.addEventListener('message', this.onMessage.bind(this))

    // Listen for user updates
    // Users.addEventListener('updated', this.onUsersUpdated)
    // this.onUsersUpdated()

    // Create grid for user zones. This grid gets updated by the Map3D class.
    // this.messageZoneGrid?.stop()
    // this.messageZoneGrid = new Grid()
    // this.messageZoneGrid.idPrefix = 'msgcell'
    // this.messageZoneGrid.cellSize = 40
    // this.messageZoneGrid.activationRange = 20
    // this.messageZoneGrid.createCell = e => new MessageCell(this)
    // this.messageZoneGrid.addEventListener('user.cell.changed', e => this.zoneChannelID = this.messageZoneGrid.userCell.id)
    // this.messageZoneGrid.start()
  }

  /** Called on stop */
  stop() {
    // clear listeners and timers
    // this.pubsub.leave(Server.serverID + ':' + Server.dimensionID)
    // Users.removeEventListener('updated', this.onUsersUpdated)
    this.messageZoneGrid?.stop()
    this.messageZoneGrid = null
    this.userChannelID = null
    this.pubsub?.shutdown()
    this.pubsub = null
  }

  /** @private Called when the users change */
  // onUsersUpdated = e => {

  //     // Get current user ID, escaped
  //     let userChannelID = 'user:' + Users.userID

  //     // Check if channel changed
  //     if (this.userChannelID === userChannelID) return
  //     if (this.userChannelID) this.pubsub.leave(this.userChannelID)
  //     this.userChannelID = userChannelID

  //     // Stop if not logged in
  //     if (!Users.userID)
  //         return

  //     // Connect to user-specific channel
  //     this.pubsub.join(this.userChannelID)

  // }

  /** @private called when a message is received */
  async onMessage(e) {
    // Add statistics
    this.numReceived += 1

    // Get message
    let channel = e.channel
    let msg = e.data

    // Skip messages if they're not for us
    // if (msg.target != '*' && msg.target != Users.userID && !Users.currentUser?.mixer_ids?.includes(msg.target))
    //     return console.log('ignored', msg)

    // Check if this is a response
    if (msg.responseID) {
      // If we have a pending broadcast assigned responseID then add result to broadcast response array
      if (this.pendingBroadcastResponses[msg.responseID]) {
        this.pendingBroadcastResponses[msg.responseID].push(msg.result)
        return
      }

      // Check if we have a pending request with this ID
      let pendingRequest = this.pendingRequests[msg.responseID]
      if (!pendingRequest) return

      // Check if error or not
      delete this.pendingRequests[msg.responseID]
      clearTimeout(pendingRequest.timeout)

      if (msg.error) {
        // Reject the pending request
        pendingRequest.rejecter(new Error(msg.error))
      } else {
        // Resolve the pending request
        pendingRequest.resolver(msg.result)
      }

      // Done handling a response
      return
    }

    // Notify listeners of this message type
    let callbacks = this.actions[msg.name] || []
    let requestResult = null
    let requestError = null
    for (let callback of callbacks) {
      // Catch errors
      let result = null
      try {
        result = await callback(msg.data, msg.from)
      } catch (ex) {
        console.error(`[MessageManager] Unhandled error in message '${msg.name}' listener: `, ex)
        requestError = null
      }

      // If this is the first response for this request, store it
      if (result && !requestResult) requestResult = result
    }

    // Stop if this is not a request
    if (!msg.requestID) return

    // Stop if we did not handle this response
    if (!requestResult && !requestError) return

    // Check for error
    if (requestError) {
      // Pass back the error
      this.pubsub.post('user:' + msg.from, {
        responseID: msg.requestID,
        from: this.userID,
        error: requestError?.message || 'There was a problem.'
      })
    } else {
      // Pass back the value
      this.pubsub.post('user:' + msg.from, {
        responseID: msg.requestID,
        from: this.userID,
        result: requestResult
      })
    }
  }

  /**
   * Send a message to the specified user, or to everyone
   *
   * @param {string} name The message name
   * @param {object} data The message payload
   * @param {string} target Recipient. Either "global" for the entire server, "nearby" for everyone in rendering range, or a user's ID for a specific user
   * @param {boolean} isRequest If true, this is a "request" and we expect a reply from the target user. The first reply message will be returned.
   * @returns {Promise<any>} If this is a request, we will return with the first reply or a timeout if no response was given.
   */
  sendMessage(name, data = {}, target = null, isRequest = false) {
    // Warn if no target provided
    if (!target)
      console.warn(
        new Error(
          `[MessageManager] sendMessage '${name}' requires a target! Defaulting to sending to nearby users only.`
        )
      )

    // Add statistics
    this.numSent += 1

    // Create payload to send
    let payload = {
      type: 'message',
      from: this.userID,
      name,
      data
    }

    // Create request ID if it's a request
    let requestPromise = null
    if (isRequest) {
      // Generate a request ID
      payload.requestID = Math.random().toString(36).substr(2)

      // Create a promise and store it
      this.pendingRequests[payload.requestID] = {}
      this.pendingRequests[payload.requestID].promise = requestPromise = new Promise(
        (resolve, reject) => {
          this.pendingRequests[payload.requestID].resolver = resolve
          this.pendingRequests[payload.requestID].rejecter = reject
        }
      )

      // Start a timer to make it time out
      this.pendingRequests[payload.requestID].timeout = setTimeout(e => {
        // Fail the request
        if (this.pendingRequests[payload.requestID]) {
          this.pendingRequests[payload.requestID].rejecter(new Error('The request timed out.'))
          delete this.pendingRequests[payload.requestID]
        }
      }, 12000)
    }

    // Get target channel
    let channel = null
    if (!target || target == 'nearby') {
      // Sending to everyone in rendering range
      channel = this.zoneChannelID
    } else if (target == 'global') {
      // Sending to the entire server
      channel = this.spaceID + ':default'
    } else {
      // Sending to a specific use
      channel = 'user:' + target
    }

    // Send it
    this.pubsub.post(channel, payload)

    // Return pending promise if any
    return requestPromise
  }

  /**
   * Send a message to the specified user, or to everyone
   *
   * @param {string} name The message name
   * @param {object} data The message payload
   * @param {string} target Recipient. Either "global" for the entire server, "nearby" (or null, the default) for everyone in rendering range, or a user's ID for a specific user
   * @param {number} duration Duration of time (milliseconds) that the message will wait for responses before it terminates
   * @returns {Array} An array of all replys recieved during the given duration is returned (empty if no replies)
   */
  async sendBroadcastMessage(name, data = {}, target = null, duration = 5000) {
    // Add statistics
    this.numSent += 1

    // Create payload to send
    let payload = {
      type: 'message',
      from: this.userID,
      name,
      data
    }

    // Generate a request ID
    payload.requestID = Math.random().toString(36).substr(2)

    // Instantiate broadcast response array with requestID
    this.pendingBroadcastResponses[payload.requestID] = []

    // Get target channel
    let channel = null
    if (!target || target == 'nearby') {
      // Sending to everyone in rendering range
      channel = this.zoneChannelID
    } else if (target == 'global') {
      // Sending to the entire server
      channel = this.spaceID + ':default'
    } else {
      // Sending to a specific use
      channel = 'user:' + target
    }

    // Send it
    this.pubsub.post(channel, payload)

    // Wait for timeout
    await new Promise(c => setTimeout(c, duration))

    // Return responses and clean up (returns empty array if no responses)
    let broadcastResponses = this.pendingBroadcastResponses[payload.requestID]
    delete this.pendingBroadcastResponses[payload.requestID]
    return broadcastResponses
  }

  // Add a listener for the specified message type
  addMessageListener(name, callback) {
    // Store callback
    this.actions[name] = this.actions[name] || []
    this.actions[name].push(callback)
  }

  // Remove a listener for the specified message type
  removeMessageListener(name) {
    // Remove callback
    this.actions[name] = null
  }

  /** Get the current state of the websocket, for debug purposes */
  get websocketState() {
    switch (this.pubsub.ws?.readyState) {
      case WebSocket.CLOSED:
        return 'closed'
      case WebSocket.CLOSING:
        return 'closing'
      case WebSocket.CONNECTING:
        return 'connecting'
      case WebSocket.OPEN:
        return 'open'
      default:
        return 'unknown'
    }
  }

  /** Get the number of messages that are waiting to be sent out */
  get queueCount() {
    return this.pubsub.queue.length
  }
}

/**
 * Represents a single zone in the message grid.
 */
// class MessageCell extends Cell {

//     /** Constructor */
//     constructor(messageManager) {
//         super()

//         // Store reference back to the MessageManager class
//         this.messageManager = messageManager

//     }

//     /** Called on load */
//     onLoad() {

//         // Subscribe
//         console.debug(`[MessageManager] Starting listener for zone: ${this.id}`)
//         this.messageManager.pubsub.join(this.id)

//     }

//     /** Called on unload */
//     onUnload() {

//         // Remove listener
//         console.debug(`[MessageManager] Removing listener for zone: ${this.id}`)
//         this.messageManager.pubsub.leave(this.id)

//     }

// }
